import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { apiFetch } from './api';

import { SignInPage, SignUpPage, ForgotPasswordPage, CheckEmailPage } from './pages/AuthPages';
import { ErrorBoundary } from './ErrorBoundary';
import OverviewPage from './pages/OverviewPage';
import KeysPage from './pages/KeysPage';
import UsagePage from './pages/UsagePage';
import BillingPage from './pages/BillingPage';
import TopupPage from './pages/TopupPage';
import DocsPage from './pages/DocsPage';
import PublicDocsPage from './pages/PublicDocsPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import PlaygroundPage from './pages/PlaygroundPage';
import CaptchaPlaygroundPage from './pages/CaptchaPlaygroundPage';
import StatusPage from './pages/StatusPage';

import { AppShell, MobileNav } from './components/AppShell';
import {
  LayoutDashboard, KeyRound, BarChart3, CreditCard, BookOpen, Settings, FlaskConical, Wallet,
  ShieldCheck,
} from 'lucide-react';

/**
 * App — root component.
 * Wires up Supabase auth, the API client, and the React Router.
 */
export default function App() {
  const [session, setSession] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initial session
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    // Auth changes
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => sub?.subscription?.unsubscribe?.();
  }, []);

  // Fetch billing when session exists
  useEffect(() => {
    if (!session) {
      setBilling(null);
      return;
    }
    refreshBilling();
  }, [session]);

  async function refreshBilling() {
    try {
      const data = await apiFetch('/v1/billing');
      setBilling(data);
    } catch (e) {
      console.warn('billing fetch failed', e);
    }
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  if (loading) {
    return <BootScreen />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/signin" element={session ? <Navigate to="/" replace /> : <SignInPage />} />
        <Route path="/signup" element={session ? <Navigate to="/" replace /> : <SignUpPage onAuthed={(u) => setSession({ user: u })} />} />
        <Route path="/forgot" element={<ForgotPasswordPage />} />
        <Route path="/check-email" element={<CheckEmailPage />} />
        <Route path="/reset" element={<ResetPasswordPage />} />
        {/* Public status page — no auth required. Lives outside the
            authed Dashboard wrapper so anyone can hit it directly. */}
        <Route path="/status" element={<StatusPage />} />
        {/* Public docs page — no auth required. Same layout for everyone. */}
        <Route path="/docs" element={<PublicDocsPage />} />
        <Route path="/docs/:slug" element={<PublicDocsPage />} />

        <Route path="/*" element={
          session ? (
            <ErrorBoundary>
              <Dashboard user={session.user} billing={billing} onSignOut={signOut} onBillingChange={refreshBilling} />
            </ErrorBoundary>
          ) : (
            <Navigate to="/signin" replace />
          )
        } />
      </Routes>
    </BrowserRouter>
  );
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setBusy(true); setError(null);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setDone(true);
      setTimeout(() => navigate('/signin', { replace: true }), 2000);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-fg">Password updated</h1>
          <p className="text-sm text-fg-muted mt-1">Redirecting to sign in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4">
        <div>
          <h1 className="text-2xl font-semibold text-fg">Set new password</h1>
          <p className="text-sm text-fg-muted mt-1">Choose a strong password.</p>
        </div>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          placeholder="New password"
          className="w-full h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand focus:ring-offset-1 focus:ring-offset-bg"
        />
        {error && <div className="px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive">{error}</div>}
        <button
          type="submit"
          disabled={busy}
          className="w-full h-9 px-4 text-sm font-medium rounded-md bg-brand text-brand-fg hover:bg-brand/90 disabled:opacity-50"
        >
          {busy ? 'Updating...' : 'Update password'}
        </button>
      </form>
    </div>
  );
}

const NAV_ALL = [
  { path: '/', label: 'Overview', icon: LayoutDashboard },
  { path: '/playground', label: 'Playground', icon: FlaskConical },
  { path: '/captcha-playground', label: 'Captcha Playground', icon: ShieldCheck },
  { path: '/usage', label: 'Logs & Analytics', icon: BarChart3 },
  { path: '/keys', label: 'API Keys', icon: KeyRound },
  { path: '/billing', label: 'Billing', icon: CreditCard },
  { path: '/topup', label: 'Top up', icon: Wallet, requiresPaid: true },
  { path: '/docs', label: 'Docs', icon: BookOpen },
  { path: '/settings', label: 'Settings', icon: Settings },
];

function Dashboard({ user, billing, onSignOut, onBillingChange }) {
  const location = useLocation();
  const NAV = NAV_ALL.filter((item) =>
    !item.requiresPaid || billing?.plan_code !== 'free'
  );
  return (
    <>
      <AppShell
        user={user}
        billing={billing}
        navigation={NAV}
        pathname={location.pathname}
        onSignOut={onSignOut}
      >
        <Routes>
          <Route path="/" element={
            <OverviewPage user={user} billing={billing} onNavigate={(path) => {
              window.history.pushState({}, '', path);
              window.dispatchEvent(new PopStateEvent('popstate'));
            }} />
          } />
          <Route path="/playground" element={<PlaygroundPage />} />
          <Route path="/captcha-playground" element={<CaptchaPlaygroundPage />} />
          <Route path="/usage" element={<UsagePage billing={billing} />} />
          <Route path="/keys" element={<KeysPage billing={billing} />} />
          <Route path="/billing" element={<BillingPage billing={billing} onBillingChange={onBillingChange} />} />
          <Route path="/topup" element={
                      // Wait for billing to load before deciding — avoid showing the
                      // page during the null/loading window.
                      billing && billing.plan_code === 'free'
                        ? <Navigate to="/billing" replace />
                        : <TopupPage billing={billing} onBillingChange={onBillingChange} />
                    } />
          <Route path="/docs" element={<DocsPage apiKey={billing?.api_key_prefix} />} />
          <Route path="/settings" element={<SettingsPage user={user} onSignOut={onSignOut} />} />
          <Route path="/profile" element={<ProfilePage user={user} billing={billing} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
      <MobileNav navigation={NAV} pathname={location.pathname} />
    </>
  );
}

function BootScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="flex items-center gap-2 text-fg-muted">
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
          <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
        <span className="text-sm">Loading...</span>
      </div>
    </div>
  );
}